/**
 * One open file, in the editor that suits it: the rich Markdown editor for
 * prose, Monaco for everything else. Panes stack into a tab group in the
 * workspace mosaic, and dragging a tab out of the stack drops the file into an
 * editor pane of its own.
 */
import type { AutomergeUrl } from "@automerge/automerge-repo";
import type { EditorSelection } from "forest/monaco-editor/MonacoEditor";

import DocPane from "@/panes/DocPane";
import SourcePane from "@/panes/SourcePane";

const MARKDOWN_EXTENSIONS = [".md", ".mdx"];

export function isMarkdownFile(filename: string): boolean {
  return MARKDOWN_EXTENSIONS.some((extension) =>
    filename.toLowerCase().endsWith(extension),
  );
}

type EditorPaneProps = {
  paneId: string;
  filename: string;
  /** Absent when the file has no Automerge doc, e.g. mounted library content. */
  url?: AutomergeUrl;
  /** Open prose in Monaco anyway, for "open as plain text". */
  plainText?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  /** Open another workspace file, e.g. a "go to definition" target. */
  onOpenPath?: (path: string, selection?: EditorSelection) => void;
  /** Where to put the cursor once this file loads (a jump landed here). */
  revealSelection?: EditorSelection;
  /** A heading to scroll to, for prose (the agent's `show` tool). */
  revealHeading?: { text: string };
  /** A comment thread to open. */
  revealThread?: { threadId: string };
};

const EditorPane = ({
  paneId,
  filename,
  url,
  plainText,
  onDirtyChange,
  onOpenPath,
  revealSelection,
  revealHeading,
  revealThread,
}: EditorPaneProps) => {
  // The rich editor writes through an Automerge doc. Markdown with none (a
  // mounted package's README) still reads fine as source, so it falls through
  // to Monaco read-only rather than claiming the file is missing.
  if (!plainText && url && isMarkdownFile(filename)) {
    return (
      <DocPane
        paneId={paneId}
        filename={filename}
        url={url}
        // Embeds (e.g. a ```sketch fence's open button) open workspace
        // files through the same seam as go-to-definition.
        onOpenFile={onOpenPath}
        revealHeading={revealHeading}
        revealThread={revealThread}
      />
    );
  }

  return (
    <SourcePane
      paneId={paneId}
      filename={filename}
      url={url}
      onDirtyChange={onDirtyChange}
      onOpenPath={onOpenPath}
      revealSelection={revealSelection}
      revealThread={revealThread}
    />
  );
};

export default EditorPane;
